import { createInjectionState } from "@vueuse/core";
import emblaCarouselVue from "embla-carousel-vue";
import { onMounted, type Ref, ref } from "vue";
import type {
    UnwrapRefCarouselApi as CarouselApi,
    CarouselEmits,
    CarouselProps,
} from "./interface";

const [useProvideCarousel, useInjectCarousel] = createInjectionState(
    ({ opts, orientation, plugins }: CarouselProps, emits: CarouselEmits) => {
        const [emblaNode, emblaApi] = emblaCarouselVue(
            {
                ...opts,
                axis: orientation === "horizontal" ? "x" : "y",
            },
            plugins,
        );
        const currentIndex: Ref<number> = ref(1);
        const slideCount: Ref<number> = ref(1);

        function scrollPrev() {
            emblaApi.value?.scrollPrev();
        }

        function scrollNext() {
            emblaApi.value?.scrollNext();
        }

        function scrollTo(number) {
            emblaApi.value?.scrollTo(number);
        }

        function setCount(count) {
            slideCount.value = count;
        }

        const canScrollNext = ref(false);
        const canScrollPrev = ref(false);

        function onSelect(api: CarouselApi) {
            currentIndex.value = api?.selectedScrollSnap() + 1;
            canScrollNext.value = api?.canScrollNext() || false;
            canScrollPrev.value = api?.canScrollPrev() || false;
        }

        onMounted(() => {
            if (!emblaApi.value) return;

            emblaApi.value?.on("init", onSelect);
            emblaApi.value?.on("reInit", onSelect);
            emblaApi.value?.on("select", onSelect);

            emits("init-api", emblaApi.value);
        });

        return {
            carouselRef: emblaNode,
            carouselApi: emblaApi,
            canScrollPrev,
            canScrollNext,
            scrollPrev,
            scrollNext,
            scrollTo,
            setCount,
            orientation,
            currentIndex,
        };
    },
);

function useCarousel() {
    const carouselState = useInjectCarousel();

    if (!carouselState)
        throw new Error("useCarousel must be used within a <Carousel />");

    return carouselState;
}

export { useCarousel, useProvideCarousel };
